import BreadCrumb from "components/Common/BreadCrumb";
import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import LabelWrapper from "components/Common/LabelWrapper";
import ModalConfirm from "components/Common/ModalConfirm";
import CountUp from "react-countup";
import { formatDateStringToKorea } from "helpers/format";
import TooltipCustom from "components/Common/TooltipCustom";
import { Link } from "react-router-dom";
import CollapseFilter from "components/Common/CollapseFilter";
import ModalMultiPreview from "components/Common/ModalMultiPreview";
import ModalPreview from "components/Common/ModalPreview";
import ContentTypeForm from "./ContentTypeForm";
import CopyWrapper from "components/Common/CopyWrapper";
import { getContentTypeList } from "store/content-type/thunk";
import contentTypeApi from "api/contentTypeApi";

const TYPE_SELECT_DEFAULT: string = "createdAt";

registerLocale("en", en);
registerLocale("ko", ko);

const ContentTypeList = () => {
  const { t, i18n } = useTranslation();

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 30),
    keyword: withDefault(StringParam, ""),
    positionCode: withDefault(StringParam, ""),
    visibleInMenu: withDefault(StringParam, ""),
    // sortBy: withDefault(StringParam, "createdAt"),
    // orderBy: withDefault(StringParam, "desc"),
  });

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [linkImage, setLinkImage] = useState<any>(null);
  const [itemToEdit, setItemToEdit] = useState<any>(false);

  const [nameSearch, setNameSearch] = useState<string>(query?.keyword || "");

  const dispatch: any = useDispatch();
  const selectLayoutState = (state: any) => state.ContentType;
  const ContentTypeProperties = createSelector(
    selectLayoutState,
    (state) => ({
      lstContentType: state.lstContentType,
      isContentTypeLoading: state.isContentTypeLoading,
      isContentTypeSuccess: state.isContentTypeSuccess,
      error: state.error,
    })
  );

  const { lstContentType, isContentTypeLoading } = useSelector(ContentTypeProperties);

  function loadTableData() {
    dispatch(getContentTypeList(query));
  }

  const searchData = () => {
    setQuery({
      ...query,
      keyword: nameSearch,
      sortBy: TYPE_SELECT_DEFAULT,
      page: 1,
      time_request: +new Date(),
    });
  };

  const resetData = () => {
    setQuery(
      {
        keyword: "",
        sortBy: TYPE_SELECT_DEFAULT,
        time_request: +new Date(),
      },
      "push"
    );

    setNameSearch((_prev) => "");
  };

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 80,
        Cell: ({ ...props }) => {
          return (
            <div style={{ minWidth: "60px" }}>
              {(lstContentType?.pagination?.total || 0) -
                ((query?.page - 1) * query?.limit + props?.row?.index)}
            </div>
          );
        },
      },
      {
        Header: t("Name"),
        accessor: "name",
        thWidth: 350,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return cell?.value ? (
            <div className="cursor-pointer">
              <CopyWrapper contentCopy={cell?.value}>
                {cell?.value}
              </CopyWrapper>
            </div>
          ): "-";
        },
      },
      {
        Header: t("Description"),
        accessor: "description",
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return cell?.value ? (
            <div className="cursor-pointer">
              <CopyWrapper contentCopy={cell?.value}>
                {cell?.value}
              </CopyWrapper>
            </div>
          ): "-";
        },
      },
      {
        Header: t("Order"),
        accessor: "ordering",
        thWidth: 100,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            <div className="cursor-pointer">
              {cell?.value}
            </div>
          )
        },
      },
      {
        Header: t("Updated at"),
        accessor: "updatedAt",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("Button Action"),
        thClass: "text-center",
        thWidth: 150,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return item?.id ? (
            <ul
              className="list-inline d-flex flex-wrap hstack gap-2 mb-0"
              style={{ justifyContent: "center" }}
            >
              <TooltipCustom
                title={t("Button Update")}
                id={`update-md-${item?.id}`}
              >
                <Link
                  className="btn btn-sm btn-soft-primary edit-item-btn"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsOpen(true);
                    setItemToEdit(item);
                  }}
                >
                  <i className="ri-pencil-fill align-bottom "></i>
                </Link>
              </TooltipCustom>

              <TooltipCustom
                title={t("Button Delete")}
                id={`delete-md-${item?.id}`}
              >
                <Link
                  className="btn btn-sm btn-soft-danger edit-item-btn"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setItemToDelete(item);
                  }}
                >
                  <i className="ri-delete-bin-3-fill align-bottom"></i>
                </Link>
              </TooltipCustom>
            </ul>
          ) : "";
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, lstContentType?.pagination?.total, query?.page]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Begin::Delete
  const handleActionDelete = async (id: any) => {
    if (!id) return;
    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await contentTypeApi.deleteContent(id);
      if (response?.code === 200 && response?.message === "Success") {
        setItemToDelete(false);
        loadTableData();
        setIsConfirmLoading((_prev) => false);
        toast(
          `${t("The content type has been deleted.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const onCloseImageClick = () => {
    setLinkImage(null);
  };

  const onCloseClick = () => {
    setItemToEdit((_prev: any) => null);
    setIsOpen((_prev) => false);
  };

  const triggerRefresh = () => {
    onCloseClick();
    loadTableData();
  };

  useEffect(() => {
    loadTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, JSON.stringify(query)]);

  useEffect(() => {
    document.title = `${t("Content Type")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Content Type")} pageTitle={t("Home")} />
          <Row>
            <Col lg={12}>
              <Card
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-5 con-sm-6">
                      <div>
                        <h5 className="card-title mb-0">
                          <span className="me-2">{t("Total")}:</span>
                          <CountUp
                            start={0}
                            end={lstContentType?.pagination?.total || 0}
                            duration={1}
                            className="text-primary"
                          />
                        </h5>
                      </div>
                    </div>
                    <div className="col-7 con-sm-6 text-end">
                      <div className="col-sm-auto">
                        <div>
                          <button
                            type="button"
                            className="btn btn-success add-btn"
                            onClick={() => {
                              setIsOpen(true);
                              setItemToEdit(null);
                            }}
                          >
                            <i className="ri-add-line align-bottom me-1"></i>
                            {t("Create Content Type")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </Row>
                </CardHeader>

                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                  <CollapseFilter>
                    <Row className="g-4 align-items-center mt-0 mt-md-2">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={t("Name")} isShow={!!nameSearch}>
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Name")}...`}
                            value={nameSearch}
                            onChange={(e) => setNameSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={9}
                        xl={9}
                        xxl={9}
                        className="hstack gap-1 justify-content-center justify-content-md-end mt-3 mt-md-3"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-1"
                            onClick={searchData}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </CollapseFilter>
                </div>
                <CardBody className="pt-3">
                  <div>
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={lstContentType?.items?.length > 0 ? lstContentType?.items : []}
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={lstContentType?.pagination?.total || 0}
                      customPageCount={Math.ceil(
                        Number(lstContentType?.pagination?.total || 0) /
                          Number(query.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sortBy: query.sortBy,
                        orderBy: query.orderBy,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isContentTypeLoading}
                    />
                  </div>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {isOpen && (
          <ContentTypeForm
            setIsOpen={setIsOpen}
            id={itemToEdit?.id}
            item={itemToEdit}
            setItemToEdit={setItemToEdit}
            triggerRefresh={triggerRefresh}
            setLinkImage={setLinkImage}
          />
        )}

        {itemToDelete && (
          <ModalConfirm
            header={t("Delete Content Type")}
            isOpen={itemToDelete}
            isLoading={isConfirmLoading}
            onClose={() => setItemToDelete(false)}
            onConfirm={() => handleActionDelete(itemToDelete?.id)}
          />
        )}

        <ModalPreview
          type={linkImage?.isVideo ? "VIDEO" : "IMAGE"}
          isOpen={!!linkImage?.url}
          url={linkImage?.url}
          content={linkImage?.content}
          onClose={() => onCloseImageClick()}
        />

        <ModalMultiPreview
          isOpen={!!linkImage?.urls}
          urls={linkImage?.urls || []}
          content={linkImage?.content}
          onClose={() => onCloseImageClick()}
        />
      </div>
    </React.Fragment>
  );
};

export default ContentTypeList;
